import * as React from "react";
import { useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { ApplicationState } from "../../store";
import * as GuestKeyRequestsState from "../../store/GuestKeyRequests";
import queryString from "query-string";
import {
  confirmPrompt,
  formatLocalTime,
  inviteClassName,
  inviteStatus,
  guestInviteWithSortedLocks,
} from "../../helpers";
import {
  GuestInviteResponse as GuestInviteType,
  LockResponse,
  SiteSystemType,
  GuestInviteLockIdResponse,
  KeyRequestCredentialType,
  GuestKeyRequestResponse,
} from "../../api/ApiClient";
import { Spinner } from "../shared/Spinner";
import Markdown from "react-markdown";
import MyAlerts, { MyAlertsRef } from "../shared/Alert";
import { useState, useEffect, useRef, useCallback } from "react";
import { useSnackbar } from "notistack";
import { Button } from "components/ui/button";

type OwnProps = {
  header?: string;
};

const validTimeFormat = "MMM DD h:mm A, YYYY";

function buttonLabel(
  credentialType: KeyRequestCredentialType,
  inProgress: boolean,
) {
  switch (credentialType) {
    case KeyRequestCredentialType.RemoteOpen:
      return inProgress ? "Opening..." : "Open";
    case KeyRequestCredentialType.MobileKey:
      return inProgress ? "Sending..." : "Send Key";
    case KeyRequestCredentialType.GetPin:
      return inProgress ? "Getting Info..." : "Get Info";
  }
}

const GuestInvite: React.FC<OwnProps> = ({ header }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const auth0 = useSelector((state: ApplicationState) => state.auth0);
  const myAlertsRef = useRef<MyAlertsRef | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  const [fetchingKeyRequest, setFetchingKeyRequest] = useState(false);
  const [inProgress, setInProgress] = useState<Record<string, boolean>>({});
  const [keyRequest, setKeyRequest] = useState<GuestInviteType | null>(null);

  const setMyAlerts = (myAlerts: MyAlertsRef) => {
    myAlertsRef.current = myAlerts;
  };

  const fetchInvite = useCallback(async () => {
    const { id: inviteId } = queryString.parse(location.search || "");

    if (!inviteId) {
      enqueueSnackbar(
        <p style={{ fontSize: "12px", padding: 0, margin: 0 }}>
          Please select an invite
        </p>,
        { variant: "error" },
      );
      return;
    }

    const keyRequestId = inviteId as string;

    setFetchingKeyRequest(true);
    try {
      const actionResult = await (dispatch as any)(
        GuestKeyRequestsState.actionCreators.fetchGuestKeyRequest({
          id: keyRequestId,
        }),
      );
      setKeyRequest(
        guestInviteWithSortedLocks(
          (actionResult as any).keyRequest as GuestInviteType,
        ),
      );
    } catch (e) {
      let errorMessage = "Error fetching invite";
      try {
        const err: any = e;
        if (err?.response) {
          const data =
            typeof err.response === "string"
              ? JSON.parse(err.response)
              : err.response;
          if (data?.errors?.length) {
            errorMessage = data.errors.map((x: any) => x.message).join(", ");
          } else if (data?.message) {
            errorMessage = data.message;
          }
        } else if (err?.message) {
          errorMessage = err.message;
        }
      } catch {
        // ignore parse errors
      }
      enqueueSnackbar(
        <p style={{ fontSize: "12px", padding: 0, margin: 0 }}>
          {errorMessage}
        </p>,
        { variant: "error" },
      );
    } finally {
      setFetchingKeyRequest(false);
    }
  }, [location.search, dispatch, enqueueSnackbar]);

  // Store the doOpen action to execute after keyRequest is set
  const [pendingOpenLock, setPendingOpenLock] =
    useState<GuestInviteLockIdResponse | null>(null);

  const doGuestOpenDoor = useCallback(
    (locks: Array<GuestInviteLockIdResponse>) => {
      if (!locks || locks.length === 0) return;

      const { session: auth0Session } = auth0;

      const credentialType = locks[0].credentialType;
      if (locks.some((l) => l.credentialType !== credentialType)) {
        enqueueSnackbar(
          <p style={{ fontSize: "12px", padding: 0, margin: 0 }}>
            Only one credential type allowed per request
          </p>,
          { variant: "error" },
        );
        return;
      }

      confirmPrompt({
        promptMessage: `Are you sure you want to open/get credential door ${locks
          .map((l) => (l.lock ? l.lock.name : ""))
          .join(", ")}?`,
        onConfirm: async () => {
          const newInProgress = locks.reduce(
            (acc, l) => ({ ...acc, [l.lock ? l.lock.id || "" : ""]: true }),
            {} as Record<string, boolean>,
          );
          setInProgress((prev) => ({ ...prev, ...newInProgress }));

          try {
            const json = await (dispatch as any)(
              GuestKeyRequestsState.actionCreators.guestOpenDoor({
                credentialType: credentialType
                  ? credentialType
                  : KeyRequestCredentialType.MobileKey,
                keyRequestId: keyRequest ? keyRequest.id || "" : "",
                lockIds: locks.map((l) => (l.lock ? l.lock.id || "" : "")),
                accessToken: auth0Session ? auth0Session.accessToken || "" : "",
              }),
            );
            const info = (json as any as GuestKeyRequestResponse)
              .tokenOrSecretInstruction;
            myAlertsRef.current &&
              myAlertsRef.current.addApiSuccessAlert(
                info ? info : "Successfully opened door or issued key",
                { dismissAfterMs: info ? undefined : 5000 },
              );
          } catch (e) {
            myAlertsRef.current && myAlertsRef.current.addApiErrorAlert(e);
          } finally {
            const completedInProgress = locks.reduce(
              (acc, l) => ({
                ...acc,
                [l.lock ? l.lock.id || "" : ""]: false,
              }),
              {} as Record<string, boolean>,
            );
            setInProgress((prev) => ({ ...prev, ...completedInProgress }));
          }
        },
      });
    },
    [auth0, keyRequest, dispatch, enqueueSnackbar],
  );

  // Handle pending open action
  useEffect(() => {
    if (pendingOpenLock && keyRequest) {
      doGuestOpenDoor([pendingOpenLock]);
      setPendingOpenLock(null);
    }
  }, [pendingOpenLock, keyRequest, doGuestOpenDoor]);

  const fetchInviteByLock = useCallback(async () => {
    const { lockId, doOpen } = queryString.parse(location.search || "");
    const {
      session: { accessToken },
    } = auth0;

    if (!accessToken || !lockId) {
      return;
    }

    setFetchingKeyRequest(true);
    try {
      const actionResult = await (dispatch as any)(
        GuestKeyRequestsState.actionCreators.fetchGuestKeyRequestByLock({
          id: lockId as string,
          accessToken,
        }),
      );
      const invite = (actionResult as any).keyRequest as GuestInviteType;
      const processedInvite = guestInviteWithSortedLocks(invite);
      setKeyRequest(processedInvite);

      // Set pending action if doOpen is true
      if (doOpen && invite && invite.lockIds) {
        const foundLock = invite.lockIds.find((l) =>
          l.lock ? l.lock.id === lockId : false,
        );
        if (foundLock && foundLock.lock) {
          setPendingOpenLock(foundLock);
        }
      }
    } catch (e) {
      let errorMessage = "Error fetching invite";
      try {
        const err: any = e;
        if (err?.response) {
          const data =
            typeof err.response === "string"
              ? JSON.parse(err.response)
              : err.response;
          if (data?.errors?.length) {
            errorMessage = data.errors.map((x: any) => x.message).join(", ");
          } else if (data?.message) {
            errorMessage = data.message;
          }
        } else if (err?.message) {
          errorMessage = err.message;
        }
      } catch {
        // ignore parse errors
      }
      enqueueSnackbar(
        <p style={{ fontSize: "12px", padding: 0, margin: 0 }}>
          {errorMessage}
        </p>,
        { variant: "error" },
      );
    } finally {
      setFetchingKeyRequest(false);
    }
  }, [location.search, auth0, dispatch, enqueueSnackbar]);

  useEffect(() => {
    const { id: inviteId } = queryString.parse(location.search || "");

    if (!inviteId) {
      fetchInviteByLock();
    } else if (inviteId) {
      fetchInvite();
    }
  }, [location.search, fetchInvite, fetchInviteByLock]);

  const renderKeyRequest = () => {
    if (!keyRequest || !keyRequest.lockIds) return null;

    const isInProgress = (lock?: LockResponse) =>
      !!inProgress[lock ? lock.id || "" : ""];

    return (
      <div>
        <table className="table table-hover">
          <thead>
            <tr>
              <th />
              <th>Site</th>
              <th>Unit</th>
            </tr>
          </thead>
          <tbody>
            {keyRequest.lockIds.map((kl, i: any) => (
              <tr key={"f_" + i}>
                <td>
                  <Button
                    type="button"
                    style={{ width: 130 }}
                    disabled={isInProgress(kl.lock)}
                    onClick={(_e: any) =>
                      keyRequest &&
                      kl.lock &&
                      doGuestOpenDoor(
                        isAssaMobileLock(kl)
                          ? getAssaMobileLocks(keyRequest, kl)
                          : [kl],
                      )
                    }
                  >
                    {buttonLabel(
                      kl.credentialType
                        ? kl.credentialType
                        : KeyRequestCredentialType.MobileKey,
                      isInProgress(kl.lock),
                    )}
                  </Button>
                </td>
                <td style={{ paddingTop: 20 }}>
                  {kl.lock && kl.lock.site ? kl.lock.site.name : "Unknown Site"}
                </td>
                <td style={{ paddingTop: 20 }}>
                  {kl.lock ? kl.lock.name : ""}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  if (fetchingKeyRequest) {
    return <Spinner />;
  }

  const now = new Date();

  const { connectUser = null, validFrom = "", validTo = "" } = keyRequest || {};

  const statusStr =
    keyRequest !== null ? inviteStatus(now, keyRequest) : "Scheduled";

  return (
    <div>
      <h3>{header}</h3>
      <hr />

      <MyAlerts ref={setMyAlerts} />

      {keyRequest && (
        <div>
          <p />
          <div>
            <b>From</b>
          </div>
          <div>
            {connectUser
              ? connectUser.name || connectUser.externalId || "unknown"
              : "unknown"}
          </div>
          <p />

          {keyRequest.details && (
            <div className="panel panel-info">
              <div className="panel-heading">Details from host:</div>
              <div className="panel-body">
                <Markdown>{keyRequest.details}</Markdown>
              </div>
              <p />
            </div>
          )}

          {(validFrom || validTo) && (
            <div>
              <div className={`text-${inviteClassName(now, keyRequest)}`}>
                <b>{statusStr}</b>
              </div>
              <div>
                {formatLocalTime(validFrom as any, validTimeFormat) ||
                  "any time"}{" "}
                -{" "}
                {formatLocalTime(validTo as any, validTimeFormat) || "any time"}
              </div>
              <p />
            </div>
          )}
        </div>
      )}
      {renderKeyRequest()}
    </div>
  );
};

function getAssaMobileLocks(
  keyRequest: GuestInviteType,
  lock: GuestInviteLockIdResponse,
): GuestInviteLockIdResponse[] {
  if (!keyRequest || !keyRequest.lockIds) return [];

  // group all locks *for the same site* into the same Assa mobile key

  return keyRequest.lockIds.filter(
    (kl) =>
      !!kl.lock &&
      isAssaMobileLock(kl) &&
      lock.lock &&
      lock.lock.site &&
      kl.lock.site &&
      kl.lock.site.id === lock.lock.site.id,
  );
}

function isAssaMobileLock(lock: GuestInviteLockIdResponse) {
  return (
    lock &&
    lock.lock &&
    lock.lock.site &&
    lock.lock.site.account &&
    lock.lock.site.account.systemType === SiteSystemType.Assa &&
    lock.credentialType === KeyRequestCredentialType.MobileKey
  );
}

export default GuestInvite;
